import type {
  ArtifactSimpleResource,
  ConfigurationModule,
  FormBuilderContext,
  FormBuilderValues,
  ModuleInitParams,
  TemplateDetails,
  TemplateDetailsFields,
  TemplateDetailsFieldsStructureChildFields,
  TemplateInfoFromList,
} from '@ingenico/react-form-builder';
import { TemplateDetailsFieldsDataTypeEnum } from '@ingenico/react-form-builder';

interface BuildModuleInitParamsProps {
  readonly configModule?: ConfigurationModule | null;
  readonly previousParams?: ModuleInitParams | null;
  readonly templateList?: TemplateInfoFromList[];
  readonly templateToDisplay?: TemplateDetails | null;
  readonly artifactListPayload: ArtifactSimpleResource[];
}

interface ShouldDisplayModuleNoInputsInfoProps {
  readonly configModule?: ConfigurationModule | null;
  readonly context?: FormBuilderContext;
  readonly isRefetchingModule?: boolean;
  readonly parameterSetIsSuccess?: boolean;
  readonly shouldDisplayLoadingOverlay?: boolean;
  readonly templateList?: TemplateInfoFromList[];
  readonly templateToDisplay?: TemplateDetails | null;
  readonly templatesIsSuccess?: boolean;
}

const MODULE_CONTEXTS: ReadonlySet<FormBuilderContext | undefined> = new Set(['module', 'readonly']);

type DataFileTemplateField = TemplateDetailsFields | TemplateDetailsFieldsStructureChildFields;

function collectDataFileResourceIds(
  field: DataFileTemplateField,
  fieldValue: ConfigurationModule['parameterSets'][number]['values'][number]['fieldValue'],
): string[] {
  if (field.dataType === TemplateDetailsFieldsDataTypeEnum.DataFile) {
    return fieldValue.type === 'SINGLE' && fieldValue.single ? [fieldValue.single] : [];
  }

  if (field.dataType !== TemplateDetailsFieldsDataTypeEnum.Structure || fieldValue.type !== 'STRUCTURE') {
    return [];
  }

  const childFields = field.definition.structure?.childFields ?? [];
  return (fieldValue.structure ?? []).flatMap((row) =>
    row.cells.flatMap((cell) => {
      const childField = childFields.find(({ id }) => id === cell.columnId);
      return childField ? collectDataFileResourceIds(childField, cell.fieldValue) : [];
    }),
  );
}

export function getDataFileResourceIds(
  configModule?: { readonly parameterSets: FormBuilderValues['parameterSetValues'] } | null,
  template?: TemplateDetails | null,
): string[] {
  if (!configModule || !template) return [];

  const fieldsById = new Map(template.parameter.fields.map((field) => [field.id, field]));
  const resourceIds = configModule.parameterSets
    .filter(({ templateId }) => templateId === template.id)
    .flatMap(({ values }) =>
      values.flatMap(({ fieldId, fieldValue }) => {
        const field = fieldsById.get(fieldId);
        return field ? collectDataFileResourceIds(field, fieldValue) : [];
      }),
    );

  return [...new Set(resourceIds)].sort();
}

function getValidTemplateList(templateList?: TemplateInfoFromList[]): TemplateInfoFromList[] {
  return (templateList ?? []).filter((template): template is TemplateInfoFromList => Boolean(template?.id));
}

function hasValidTemplateToDisplay(templateToDisplay?: TemplateDetails | null): boolean {
  return Boolean(templateToDisplay?.parameter?.containers?.[0]?.id);
}

export function buildModuleInitParams({
  configModule,
  previousParams,
  templateList,
  templateToDisplay,
  artifactListPayload,
}: BuildModuleInitParamsProps): ModuleInitParams | undefined {
  const resolvedTemplateList = getValidTemplateList(templateList ?? previousParams?.templateList);

  if (
    !configModule ||
    !templateToDisplay ||
    !hasValidTemplateToDisplay(templateToDisplay) ||
    resolvedTemplateList.length === 0
  ) {
    return undefined;
  }

  return {
    mode: 'module',
    templateList: resolvedTemplateList,
    configModule,
    templateToDisplay,
    artifacts: artifactListPayload,
  };
}

export function shouldDisplayModuleNoInputsInfo({
  configModule,
  context,
  isRefetchingModule,
  parameterSetIsSuccess,
  shouldDisplayLoadingOverlay,
  templateList,
  templateToDisplay,
  templatesIsSuccess,
}: ShouldDisplayModuleNoInputsInfoProps): boolean {
  if (
    !MODULE_CONTEXTS.has(context) ||
    !templatesIsSuccess ||
    !parameterSetIsSuccess ||
    !configModule ||
    shouldDisplayLoadingOverlay ||
    isRefetchingModule
  ) {
    return false;
  }

  return !hasValidTemplateToDisplay(templateToDisplay) || getValidTemplateList(templateList).length === 0;
}
